<template>
  <div id="layout-wrapper" :class="{ 'nav-collapsed': isNavCollapsed }">
    <NavigationBar @nav-toggle="handleNavToggle" />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Leave Calendar</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Leave Calendar</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading calendar...</p>
          </div>

          <div v-else-if="calendarData">
            <!-- Calendar Controls -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-4">
                        <h5 class="mb-0">
                          <i class="mdi mdi-calendar-month text-primary me-2"></i>
                          {{ calendarData.period.monthName }} {{ calendarData.period.year }}
                        </h5>
                      </div>
                      <div class="col-md-4 text-center mt-3 mt-md-0">
                        <div class="btn-group">
                          <button @click="previousMonth" class="btn btn-outline-primary">
                            <i class="mdi mdi-chevron-left"></i>
                          </button>
                          <button @click="goToToday" class="btn btn-outline-primary">
                            Today
                          </button>
                          <button @click="nextMonth" class="btn btn-outline-primary">
                            <i class="mdi mdi-chevron-right"></i>
                          </button>
                        </div>
                      </div>
                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <button @click="gotTo('employee-leave-apply')" class="btn btn-primary">
                          <i class="mdi mdi-plus me-1"></i>
                          Apply for Leave
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Statistics -->
            <div class="row">
              <div class="col-xl-2 col-md-4 col-6">
                <div class="card mini-stats-card">
                  <div class="card-body">
                    <div class="text-center">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title rounded-circle bg-primary-subtle text-primary">
                          <i class="mdi mdi-calendar"></i>
                        </span>
                      </div>
                      <h5 class="mb-0">{{ calendarData.statistics.weekdays }}</h5>
                      <p class="text-muted small mb-0">Weekdays</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-2 col-md-4 col-6">
                <div class="card mini-stats-card">
                  <div class="card-body">
                    <div class="text-center">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title rounded-circle bg-success-subtle text-success">
                          <i class="mdi mdi-check-circle"></i>
                        </span>
                      </div>
                      <h5 class="mb-0">{{ calendarData.statistics.presentDays }}</h5>
                      <p class="text-muted small mb-0">Present</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-2 col-md-4 col-6">
                <div class="card mini-stats-card">
                  <div class="card-body">
                    <div class="text-center">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title rounded-circle bg-info-subtle text-info">
                          <i class="mdi mdi-airplane"></i>
                        </span>
                      </div>
                      <h5 class="mb-0">{{ calendarData.statistics.leaveDays }}</h5>
                      <p class="text-muted small mb-0">On Leave</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-2 col-md-4 col-6">
                <div class="card mini-stats-card">
                  <div class="card-body">
                    <div class="text-center">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title rounded-circle bg-warning-subtle text-warning">
                          <i class="mdi mdi-clock-outline"></i>
                        </span>
                      </div>
                      <h5 class="mb-0">{{ calendarData.statistics.pendingLeaveDays }}</h5>
                      <p class="text-muted small mb-0">Pending</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-2 col-md-4 col-6">
                <div class="card mini-stats-card">
                  <div class="card-body">
                    <div class="text-center">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title rounded-circle bg-danger-subtle text-danger">
                          <i class="mdi mdi-close-circle"></i>
                        </span>
                      </div>
                      <h5 class="mb-0">{{ calendarData.statistics.absentDays }}</h5>
                      <p class="text-muted small mb-0">Absent</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-2 col-md-4 col-6">
                <div class="card mini-stats-card">
                  <div class="card-body">
                    <div class="text-center">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title rounded-circle bg-secondary-subtle text-secondary">
                          <i class="mdi mdi-star"></i>
                        </span>
                      </div>
                      <h5 class="mb-0">{{ calendarData.statistics.holidays }}</h5>
                      <p class="text-muted small mb-0">Holidays</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Legend -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap gap-3 justify-content-center">
                      <div class="legend-item">
                        <span class="legend-box bg-success"></span>
                        <span class="text-muted small">Present</span>
                      </div>
                      <div class="legend-item">
                        <span class="legend-box bg-info"></span>
                        <span class="text-muted small">Approved Leave</span>
                      </div>
                      <div class="legend-item">
                        <span class="legend-box bg-warning"></span>
                        <span class="text-muted small">Pending Leave</span>
                      </div>
                      <div class="legend-item">
                        <span class="legend-box bg-danger"></span>
                        <span class="text-muted small">Absent</span>
                      </div>
                      <div class="legend-item">
                        <span class="legend-box bg-secondary"></span>
                        <span class="text-muted small">Holiday</span>
                      </div>
                      <div class="legend-item">
                        <span class="legend-box bg-light border"></span>
                        <span class="text-muted small">Weekend</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Calendar Grid -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body p-0">
                    <div class="calendar-grid">
                      <!-- Day Headers -->
                      <div class="calendar-header">
                        <div class="calendar-day-header">Mon</div>
                        <div class="calendar-day-header">Tue</div>
                        <div class="calendar-day-header">Wed</div>
                        <div class="calendar-day-header">Thu</div>
                        <div class="calendar-day-header">Fri</div>
                        <div class="calendar-day-header">Sat</div>
                        <div class="calendar-day-header">Sun</div>
                      </div>

                      <!-- Calendar Days -->
                      <div class="calendar-body">
                        <!-- Empty cells for padding -->
                        <div v-for="(emptyCell, index) in getFirstDayOffset()" :key="'empty-' + index" class="calendar-day empty"></div>

                        <!-- Actual days -->
                        <div
                            v-for="day in calendarData.calendarDays"
                            :key="day.date"
                            class="calendar-day"
                            :class="getDayClasses(day)"
                            @click="showDayDetails(day)">

                          <div class="day-number">{{ day.day }}</div>

                          <div class="day-events">
                            <!-- Holiday -->
                            <div v-for="event in getHolidayEvents(day)" :key="'holiday-' + event.name" class="event-badge holiday">
                              <i class="mdi mdi-star"></i>
                              <span class="event-text">{{ truncateText(event.name, 15) }}</span>
                            </div>

                            <!-- Leave -->
                            <div v-for="event in getLeaveEvents(day)" :key="'leave-' + event.leaveId"
                                 class="event-badge"
                                 :class="event.status === 'approved' ? 'leave-approved' : 'leave-pending'">
                              <i class="mdi mdi-airplane"></i>
                              <span class="event-text">{{ truncateText(event.name, 12) }}</span>
                            </div>

                            <!-- Attendance -->
<!--                            eslint-disable-->
                            <div v-for="event in getAttendanceEvents(day)" :key="'attendance-' + day.date"
                                 class="event-badge attendance">
                              <i class="mdi mdi-check-circle"></i>
                              <span class="event-text">{{ event.hours }}h</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Upcoming Events -->
            <div class="row">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h6 class="card-title mb-0">
                      <i class="mdi mdi-calendar-star text-primary me-2"></i>
                      Upcoming Holidays
                    </h6>
                  </div>
                  <div class="card-body">
                    <div v-if="calendarData.holidays.length === 0" class="text-center text-muted py-3">
                      No holidays this month
                    </div>
                    <div v-else>
                      <div v-for="holiday in calendarData.holidays" :key="holiday.id" class="d-flex mb-3 pb-3 border-bottom">
                        <div class="flex-shrink-0">
                          <div class="avatar-xs">
                            <span class="avatar-title bg-secondary-subtle text-secondary rounded">
                              <i class="mdi mdi-star"></i>
                            </span>
                          </div>
                        </div>
                        <div class="flex-grow-1 ms-3">
                          <h6 class="mb-1">{{ holiday.holiday_name }}</h6>
                          <p class="text-muted mb-0 small">
                            <i class="mdi mdi-calendar me-1"></i>
                            {{ formatDate(holiday.holiday_date) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h6 class="card-title mb-0">
                      <i class="mdi mdi-airplane text-info me-2"></i>
                      Approved Leaves This Month
                    </h6>
                  </div>
                  <div class="card-body">
                    <div v-if="calendarData.leaves.length === 0" class="text-center text-muted py-3">
                      No approved leaves this month
                    </div>
                    <div v-else>
                      <div v-for="leave in calendarData.leaves" :key="leave.id" class="d-flex mb-3 pb-3 border-bottom">
                        <div class="flex-shrink-0">
                          <div class="avatar-xs">
                            <span class="avatar-title bg-info-subtle text-info rounded">
                              <i class="mdi mdi-airplane"></i>
                            </span>
                          </div>
                        </div>
                        <div class="flex-grow-1 ms-3">
                          <h6 class="mb-1">{{ leave.leaveTypeName }}</h6>
                          <p class="text-muted mb-0 small">
                            <i class="mdi mdi-calendar-range me-1"></i>
                            {{ formatDate(leave.startDate) }} - {{ formatDate(leave.endDate) }}
                            <span class="badge bg-info ms-2">{{ leave.totalDays }} days</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>

    <!-- Day Details Modal -->
    <div class="modal fade" id="dayDetailsModal" tabindex="-1" ref="dayDetailsModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" v-if="selectedDay">
              <i class="mdi mdi-calendar-day me-2"></i>
              {{ formatDateLong(selectedDay.date) }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)"></button>
          </div>
          <div class="modal-body" v-if="selectedDay">
            <!-- Day Status -->
            <div class="alert" :class="getDayStatusAlertClass(selectedDay)">
              <h6 class="alert-heading">
                <i :class="getDayStatusIcon(selectedDay)"></i>
                {{ getDayStatus(selectedDay) }}
              </h6>
            </div>

            <!-- Events -->
            <div v-if="selectedDay.events.length > 0">
              <h6 class="mb-3">Events for this day:</h6>

              <div v-for="(event, index) in selectedDay.events" :key="index" class="mb-3 p-3 border rounded">
                <!-- Holiday -->
                <div v-if="event.type === 'holiday'">
                  <div class="d-flex align-items-start">
                    <i class="mdi mdi-star text-secondary font-size-20 me-2"></i>
                    <div>
                      <h6 class="mb-1">{{ event.name }}</h6>
                      <p class="text-muted mb-0 small">{{ event.description }}</p>
                      <span v-if="event.isPublic == '1'" class="badge bg-secondary mt-1">Public Holiday</span>
                    </div>
                  </div>
                </div>

                <!-- Leave -->
                <div v-if="event.type === 'leave'">
                  <div class="d-flex align-items-start">
                    <i class="mdi mdi-airplane font-size-20 me-2"
                       :class="event.status === 'approved' ? 'text-info' : 'text-warning'"></i>
                    <div>
                      <h6 class="mb-1">{{ event.name }}</h6>
                      <span class="badge" :class="event.status === 'approved' ? 'bg-info' : 'bg-warning'">
                        {{ event.status }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Attendance -->
                <div v-if="event.type === 'attendance'">
                  <div class="d-flex align-items-start">
                    <i class="mdi mdi-clock-check text-success font-size-20 me-2"></i>
                    <div>
                      <h6 class="mb-1">Attendance</h6>
                      <p class="text-muted mb-1 small">
                        <strong>Check In:</strong> {{ event.checkIn || 'N/A' }}<br>
                        <strong>Check Out:</strong> {{ event.checkOut || 'N/A' }}<br>
                        <strong>Hours:</strong> {{ event.hours || '0' }}h
                      </p>
                      <span class="badge bg-success">{{ event.status }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center text-muted py-3">
              <i class="mdi mdi-information-outline font-size-24"></i>
              <p class="mb-0">No events for this day</p>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>

/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeLeaveCalendar",
  components: { NavigationBar },

  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      calendarData: null,
      selectedMonth: new Date().getMonth() + 1,
      selectedYear: new Date().getFullYear(),
      selectedDay: null,
      dayDetailsModalInstance: null
    };
  },

  mounted() {
    this.cleanupBootstrapModalArtifacts();
    this.loadCalendar();
    this.initDayDetailsModal();
  },

  beforeUnmount() {
    if (this.dayDetailsModalInstance) {
      this.dayDetailsModalInstance.hide();
      this.dayDetailsModalInstance.dispose();
      this.dayDetailsModalInstance = null;
    }
    this.cleanupBootstrapModalArtifacts();
  },

  methods: {
    cleanupBootstrapModalArtifacts() {
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    },

    closeModalFromEvent(event) {
      event?.preventDefault?.();
      if (this.dayDetailsModalInstance) {
        this.dayDetailsModalInstance.hide();
      }
      this.$nextTick(() => this.cleanupBootstrapModalArtifacts());
    },

    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    async loadCalendar() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeLeaveCalendar");
        data.append("month", String(this.selectedMonth).padStart(2, '0'));
        data.append("year", this.selectedYear);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.calendarData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load calendar');
        }
      } catch (error) {
        this.$toast?.error("Failed to load calendar: " + error.message) ||
        console.error("Failed to load calendar:", error);
      } finally {
        this.loading = false;
      }
    },

    initDayDetailsModal() {
      const modalElement = this.$refs.dayDetailsModal;
      if (modalElement && typeof bootstrap !== 'undefined') {
        this.dayDetailsModalInstance = new bootstrap.Modal(modalElement);
      }
    },

    getFirstDayOffset() {
      // Returns the number of empty cells needed before the first day
      // Returns an array so we can use v-for with proper index
      if (!this.calendarData || this.calendarData.calendarDays.length === 0) return [];

      const firstDay = this.calendarData.calendarDays[0];
      const offset = firstDay.dayOfWeek - 1; // Offset for Monday start

      // Return an array of the correct length
      return Array(offset).fill(null);
    },

    getDayClasses(day) {
      const classes = [];

      if (day.isToday) classes.push('today');
      if (day.isWeekend) classes.push('weekend');
      if (day.isPast) classes.push('past');
      if (day.isFuture) classes.push('future');

      // Check for events
      const hasHoliday = day.events.some(e => e.type === 'holiday');
      const hasLeave = day.events.some(e => e.type === 'leave' && e.status === 'approved');
      const hasPendingLeave = day.events.some(e => e.type === 'leave' && e.status === 'pending');
      const hasAttendance = day.events.some(e => e.type === 'attendance');

      if (hasHoliday) classes.push('has-holiday');
      if (hasLeave) classes.push('has-leave');
      if (hasPendingLeave) classes.push('has-pending-leave');
      if (hasAttendance) classes.push('has-attendance');

      // Absent logic (weekday, past, no attendance, no leave)
      if (!day.isWeekend && day.isPast && !hasAttendance && !hasLeave && !day.isToday) {
        classes.push('absent');
      }

      return classes.join(' ');
    },

    getHolidayEvents(day) {
      return day.events.filter(e => e.type === 'holiday');
    },

    getLeaveEvents(day) {
      return day.events.filter(e => e.type === 'leave');
    },

    getAttendanceEvents(day) {
      return day.events.filter(e => e.type === 'attendance');
    },

    getDayStatus(day) {
      if (day.isWeekend) return 'Weekend';

      const hasHoliday = day.events.some(e => e.type === 'holiday');
      if (hasHoliday) return 'Holiday';

      const hasLeave = day.events.some(e => e.type === 'leave' && e.status === 'approved');
      if (hasLeave) return 'On Leave';

      const hasPendingLeave = day.events.some(e => e.type === 'leave' && e.status === 'pending');
      if (hasPendingLeave) return 'Pending Leave Request';

      const hasAttendance = day.events.some(e => e.type === 'attendance');
      if (hasAttendance) return 'Present';

      if (day.isPast && !day.isToday) return 'Absent';
      if (day.isToday) return 'Today';
      if (day.isFuture) return 'Future Date';

      return 'No Data';
    },

    getDayStatusIcon(day) {
      if (day.isWeekend) return 'mdi mdi-calendar-weekend';

      const hasHoliday = day.events.some(e => e.type === 'holiday');
      if (hasHoliday) return 'mdi mdi-star';

      const hasLeave = day.events.some(e => e.type === 'leave' && e.status === 'approved');
      if (hasLeave) return 'mdi mdi-airplane';

      const hasPendingLeave = day.events.some(e => e.type === 'leave' && e.status === 'pending');
      if (hasPendingLeave) return 'mdi mdi-clock-outline';

      const hasAttendance = day.events.some(e => e.type === 'attendance');
      if (hasAttendance) return 'mdi mdi-check-circle';

      if (day.isPast && !day.isToday) return 'mdi mdi-close-circle';

      return 'mdi mdi-calendar';
    },

    getDayStatusAlertClass(day) {
      if (day.isWeekend) return 'alert-secondary';

      const hasHoliday = day.events.some(e => e.type === 'holiday');
      if (hasHoliday) return 'alert-secondary';

      const hasLeave = day.events.some(e => e.type === 'leave' && e.status === 'approved');
      if (hasLeave) return 'alert-info';

      const hasPendingLeave = day.events.some(e => e.type === 'leave' && e.status === 'pending');
      if (hasPendingLeave) return 'alert-warning';

      const hasAttendance = day.events.some(e => e.type === 'attendance');
      if (hasAttendance) return 'alert-success';

      if (day.isPast && !day.isToday) return 'alert-danger';

      return 'alert-info';
    },

    showDayDetails(day) {
      this.selectedDay = day;
      if (this.dayDetailsModalInstance) {
        this.dayDetailsModalInstance.show();
      }
    },

    previousMonth() {
      this.selectedMonth--;
      if (this.selectedMonth < 1) {
        this.selectedMonth = 12;
        this.selectedYear--;
      }
      this.loadCalendar();
    },

    nextMonth() {
      this.selectedMonth++;
      if (this.selectedMonth > 12) {
        this.selectedMonth = 1;
        this.selectedYear++;
      }
      this.loadCalendar();
    },

    goToToday() {
      this.selectedMonth = new Date().getMonth() + 1;
      this.selectedYear = new Date().getFullYear();
      this.loadCalendar();
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatDateLong(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
#layout-wrapper {
  min-height: 100vh;
  box-sizing: border-box;
}

.content-minimize {
  margin-left: 280px;
  padding: 20px;
  min-height: 100vh;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

#layout-wrapper.nav-collapsed .content-minimize {
  margin-left: 70px;
}

@media (max-width: 1024px) {
  .content-minimize {
    margin-left: 70px;
  }
}

@media (max-width: 768px) {
  .content-minimize {
    margin-left: 0;
    padding: 15px;
  }
  #layout-wrapper.nav-collapsed .content-minimize {
    margin-left: 0;
  }
}

.mini-stats-card {
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.mini-stats-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.avatar-xs {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-secondary-subtle {
  background-color: rgba(108, 117, 125, 0.1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-box {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: inline-block;
}

/* Calendar Grid */
.calendar-grid {
  display: flex;
  flex-direction: column;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.calendar-day-header {
  padding: 12px;
  text-align: center;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
}

.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background-color: #dee2e6;
}

.calendar-day {
  background-color: white;
  min-height: 100px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  flex-direction: column;
}

.calendar-day:hover {
  background-color: #f8f9fa;
  transform: scale(1.02);
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.calendar-day.empty {
  background-color: #f8f9fa;
  cursor: default;
}

.calendar-day.empty:hover {
  transform: none;
  box-shadow: none;
}

.calendar-day.weekend {
  background-color: #f8f9fa;
}

.calendar-day.today {
  border: 2px solid #5b73e8;
  box-shadow: 0 0 0 2px rgba(91, 115, 232, 0.2);
}

.calendar-day.past {
  opacity: 0.8;
}

.calendar-day.has-holiday {
  background-color: #fff9e6;
}

.calendar-day.has-leave {
  background-color: #e7f3ff;
}

.calendar-day.has-pending-leave {
  background-color: #fff8e1;
}

.calendar-day.has-attendance {
  background-color: #f0f9f4;
}

.calendar-day.absent {
  background-color: #ffe9e9;
}

.day-number {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 4px;
  color: #495057;
}

.calendar-day.today .day-number {
  color: #5b73e8;
}

.day-events {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
}

.event-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
}

.event-badge i {
  font-size: 12px;
  flex-shrink: 0;
}

.event-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-badge.holiday {
  background-color: #6c757d;
  color: white;
}

.event-badge.leave-approved {
  background-color: #17a2b8;
  color: white;
}

.event-badge.leave-pending {
  background-color: #ffc107;
  color: #000;
}

.event-badge.attendance {
  background-color: #28a745;
  color: white;
}

@media (max-width: 768px) {
  .calendar-day {
    min-height: 80px;
    padding: 4px;
  }

  .calendar-day-header {
    padding: 8px 4px;
    font-size: 0.75rem;
  }

  .day-number {
    font-size: 0.875rem;
  }

  .event-badge {
    font-size: 0.65rem;
    padding: 2px 4px;
  }
}
</style>